import {Outlet} from 'react-router-dom'
import {ContentArea} from '@/components/shared/ContentArea'
import {usePageTitle} from '@/hooks/use-page-title'
import {useI18n} from '@/i18n'
import {InlineRow} from '@/components/shared/InlineRow'
import {SidebarNav, type SidebarNavItem} from '@/components/layout/SidebarNav'
import {SidebarNavId} from '@/testids/SidebarNavTestIds'
import {SectionContainer} from '@/components/shared/SectionContainer'
import {routes} from '@/lib/routes'
import {CONCEPT_ICON} from '@/lib/concepts'
import {CountBadge} from '@/components/shared/CountBadge'
import {useAuth} from '@/contexts/auth-context'
import {permissions} from '@/lib/permissions'
import {usePendingMineRehireReviewCount} from '@/hooks/use-rehire-review'

interface MyMenuItem extends SidebarNavItem {
    permission?: string
}

export function My() {
    const i18n = useI18n()
    usePageTitle(i18n.my.pageTitle)
    const {hasPermission} = useAuth()
    const {data: rehireReviewData} = usePendingMineRehireReviewCount()
    const pendingMyRehiresCount = rehireReviewData?.count ?? 0

    const sectionMyAccount = String(i18n.my.sectionMyAccount)
    const sectionAsManager = String(i18n.my.sectionAsManager)

    const menuItems: MyMenuItem[] = [
        {
            id: SidebarNavId.MyDashboard,
            href: routes.my.root(),
            icon: CONCEPT_ICON.dashboard,
            title: i18n.my.dashboard,
            description: i18n.my.dashboardDesc,
            end: true,
            section: sectionMyAccount,
        },
        {
            id: SidebarNavId.MyAccess,
            href: routes.my.access.path(),
            icon: CONCEPT_ICON.account,
            title: i18n.my.myAccess,
            description: i18n.my.myAccessDesc,
            section: sectionMyAccount,
        },
        {
            id: SidebarNavId.MyResponsibilities,
            href: routes.my.responsibilities.path(),
            icon: CONCEPT_ICON.ownership,
            title: i18n.my.myResponsibilities,
            description: i18n.my.myResponsibilitiesDesc,
            section: sectionMyAccount,
        },
        {
            id: SidebarNavId.MyRequests,
            href: routes.my.requests.list(),
            icon: CONCEPT_ICON.myRequest,
            title: i18n.my.myRequests,
            description: i18n.my.myRequestsDesc,
            section: sectionMyAccount,
        },
        {
            id: SidebarNavId.MyDelegations,
            href: routes.my.delegations.path(),
            icon: CONCEPT_ICON.delegation,
            title: i18n.my.myDelegations,
            description: i18n.my.myDelegationsDesc,
            section: sectionMyAccount,
        },
        {
            id: SidebarNavId.MyNotifications,
            href: routes.my.notifications.path(),
            icon: CONCEPT_ICON.notification,
            title: i18n.my.myNotifications,
            description: i18n.my.myNotificationsDesc,
            section: sectionMyAccount,
        },
        {
            id: SidebarNavId.MyApprovals,
            href: routes.my.approvals.path(),
            icon: CONCEPT_ICON.myApproval,
            title: i18n.my.myApprovals,
            description: i18n.my.myApprovalsDesc,
            section: sectionAsManager,
        },
        {
            id: SidebarNavId.MyReviews,
            href: routes.my.reviews.list(),
            icon: CONCEPT_ICON.certificationReview,
            title: i18n.my.myReviews,
            description: i18n.my.myReviewsDesc,
            permission: permissions.certifications.review,
            section: sectionAsManager,
        },
        {
            id: SidebarNavId.MyRehires,
            href: routes.my.rehires.list(),
            icon: CONCEPT_ICON.rehireReview,
            title: i18n.my.myRehires,
            description: i18n.my.myRehiresDesc,
            permission: permissions.rehireReview.listMine,
            badge: <CountBadge count={pendingMyRehiresCount} variant="primary" data-testid="my-rehires-badge"/>,
            section: sectionAsManager,
        },
    ]

    const visibleMenuItems = menuItems.filter(item =>
        item.permission === undefined || hasPermission(item.permission)
    )

    return (
        <SectionContainer>
            <InlineRow align="start">
                <SidebarNav items={visibleMenuItems} aria-label={String(i18n.my.pageTitle)}/>

                {/* Content Area */}
                <ContentArea>
                    <Outlet/>
                </ContentArea>
            </InlineRow>
        </SectionContainer>
    )
}
